import classNames from 'classnames';
import { DragEventHandler, MouseEventHandler, useCallback, useRef, useState } from 'react';

import useStyles from './styles';
import { IUploadArea } from './types';

function UploadArea({ className, children, disabled, accept, multiple, onUpload }: IUploadArea) {
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [dragHovering, setDragHovering] = useState<boolean>(false);

  const onAreaDrag: (hovering: boolean) => DragEventHandler<HTMLDivElement> = useCallback(
    (hovering) => (event) => {
      event.stopPropagation();
      event.preventDefault();

      if (disabled) {
        return;
      }

      setDragHovering(hovering);
    },
    [disabled],
  );

  const onInputUpload = useCallback(() => {
    if (fileInputRef.current?.files) {
      onUpload(fileListToArray(fileInputRef.current.files), 'button');
    }
  }, [onUpload]);

  const onDropUpload: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();

      if (disabled) {
        return;
      }

      setDragHovering(false);
      onUpload(fileListToArray(event.dataTransfer.files), 'drag_and_drop');
    },
    [disabled, onUpload],
  );

  // Handle click - programmatically trigger file input
  // This avoids hydration mismatch issues with htmlFor/id
  const handleAreaClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (disabled) {
        return;
      }

      // Only trigger if clicking on the area itself, not on the input
      if (event.target === event.currentTarget || !(event.target as HTMLElement).closest('input')) {
        fileInputRef.current?.click();
      }
    },
    [disabled],
  );

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-testid="upload-area"
      className={classNames(classes.root, { hovering: dragHovering }, className)}
      onDragOver={onAreaDrag(true)}
      onDragLeave={onAreaDrag(false)}
      onDrop={onDropUpload}
      onClick={handleAreaClick}
      onKeyDown={(e) => {
        if (disabled) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fileInputRef.current?.click();
        }
      }}
    >
      <input
        className={classes.input}
        type="file"
        ref={fileInputRef}
        onChange={onInputUpload}
        accept={accept}
        multiple={multiple}
      />
      {children}
    </div>
  );
}

function fileListToArray(fileList: FileList) {
  const filesArray = [];
  for (let i = 0; i < fileList.length; i++) {
    filesArray.push(fileList[i]);
  }
  return filesArray;
}

export default UploadArea;
